import type { SiteRef, SupplierRef } from './types';

/**
 * Istoricul de prețuri: singurul lucru din Procumi care crește singur.
 *
 * Fiecare preț ofertat vreodată rămâne în bază legat de poziția lui. Aici se
 * adună pe produs, ca data viitoare să știi ce ai plătit și cui — informația
 * pe care un dosar cu PDF-uri nu ți-o dă niciodată.
 *
 * Gruparea se face pe descriere normalizată **plus unitate de măsură**: „ciment
 * 42,5R" la sac și la tonă sunt două prețuri care n-au voie să stea împreună.
 */

/** Textul redus la ce contează pentru potrivire: fără diacritice, fără semne. */
function normalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Aceeași unitate, scrisă cum o scrie fiecare: „l” și „litri”, „buc.” și
 * „bucăți”. „ml” nu intră: pe șantier e metru liniar, dar poate fi și mililitru.
 */
const UNIT_SYNONYMS: Record<string, string> = {
  l: 'l',
  litri: 'l',
  litru: 'l',
  lt: 'l',
  buc: 'buc',
  bucati: 'buc',
  bucata: 'buc',
  bc: 'buc',
  t: 't',
  to: 't',
  tona: 't',
  tone: 't',
  kg: 'kg',
  kilogram: 'kg',
  kilograme: 'kg',
  mp: 'mp',
  m2: 'mp',
  'metri patrati': 'mp',
  mc: 'mc',
  m3: 'mc',
  'metri cubi': 'mc',
  m: 'm',
  metru: 'm',
  metri: 'm',
  sac: 'sac',
  saci: 'sac',
  // în engleză
  pcs: 'buc',
  pc: 'buc',
  piece: 'buc',
  pieces: 'buc',
  litre: 'l',
  litres: 'l',
  tonne: 't',
  tonnes: 't',
  bag: 'sac',
  bags: 'sac',
};

/** Unitatea redusă la forma ei obișnuită. Exponentul rămâne cifră: m² nu e m. */
function normalizeUnit(unit: string): string {
  const u = normalize(unit.replace(/²/g, '2').replace(/³/g, '3'));
  return UNIT_SYNONYMS[u] ?? u;
}

/**
 * Cheia sub care se adună același produs din cereri diferite.
 * „Cărămidă POROTHERM 25" și „caramida porotherm  25" sunt același produs;
 * aceeași cărămidă la „buc" și la „palet" nu sunt.
 */
export function productKey(desc: string, unit: string): string {
  return `${normalize(desc)}|${normalizeUnit(unit)}`;
}

/** Un preț ofertat, așa cum vine din bază. */
export interface PriceQuote {
  /** preț unitar în lei, fără TVA */
  price: number;
  supplier: SupplierRef;
  /** ISO 8601 — când a fost primită oferta */
  at: string;
  rfqId: string;
  rfqTitle: string;
  site: SiteRef;
  /** descrierea exactă din cererea aceea */
  desc: string;
  unit: string;
  /** cantitatea cerută atunci */
  qty: number;
}

/** Ordonare descrescătoare după dată; la egalitate, prețul mai mic primul. */
const recentFirst = (a: PriceQuote, b: PriceQuote): number =>
  a.at === b.at ? a.price - b.price : a.at < b.at ? 1 : -1;

/** Toate prețurile unui produs, cel mai recent primul. */
export function historyOf(quotes: PriceQuote[], key: string): PriceQuote[] {
  return quotes.filter((q) => productKey(q.desc, q.unit) === key).sort(recentFirst);
}
